import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  Grid,
  TextField,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Avatar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  Visibility,
  Edit,
  Delete,
  Person,
  LocationOn,
  CreditCard,
  Close,
  Business,
  Description,
  Add,
  Refresh as RefreshIcon,
  Image as ImageIcon,
  PictureAsPdf,
  ReceiptLong,
  AccountBalanceWallet,
  Straighten,
  AccountBalance,
  Policy,
  Map as MapIcon,
  LocationCity,
  Smartphone,
  Email as EmailIcon,
  CreditCard as CreditCardIcon,
  History as HistoryIcon,
  AttachFile,
} from '@mui/icons-material';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { exportAsPDF as exportPDFUtil, exportAsExcel, exportAsCSV as exportCSVUtil } from '../../utils/importExport';
import AlertModal from '../../components/AlertModal';
import ConfirmModal from '../../components/ConfirmModal';

import AddPurchasePage from './AddPurchase';
import { getAllLandOwnershipRecords, deleteLandOwnership, getLandOwnershipRecordById } from '../../services/purchase';
import { hasWritePermission, hasReadPermission } from '../../utils/menuFilter';
import { useHistory } from 'react-router-dom';
import WarningIcon from '@mui/icons-material/Warning';

const Purchase = () => {
  const history = useHistory();
  const canWritePurchase = hasWritePermission('purchase.purchase') || hasWritePermission('purchase');
  const canReadPurchase = hasReadPermission('purchase.purchase') || hasReadPermission('purchase');
  const [view, setView] = useState('list');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);

  const [purchaseData, setPurchaseData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageDialogUrl, setImageDialogUrl] = useState('');
  const [imageDialogTitle, setImageDialogTitle] = useState('');
  const [brokenImages, setBrokenImages] = useState({});

  // Centralized Modal States
  const [modalState, setModalState] = useState({
    alert: { open: false, title: '', message: '' },
    confirm: { open: false, title: '', message: '', onConfirm: null, type: 'info' }
  });

  const showAlert = (message, title = 'Alert') => {
    setModalState(prev => ({ ...prev, alert: { open: true, title, message } }));
  };

  const showConfirm = (message, onConfirm, title = 'Confirm', type = 'info') => {
    setModalState(prev => ({ ...prev, confirm: { open: true, title, message, onConfirm, type } }));
  };

  const handleOpenAdd = () => {
    setSelectedPurchase(null);
    setView('add');
  };

  const handleOpenEdit = (item) => {
    // Find the original item from the full dataset to ensure we have complete data
    const originalItem = purchaseData.find(p => p.id === item.id);
    setSelectedPurchase(originalItem || item);
    setView('edit');
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedPurchase(null);
  };

  const handleReset = () => {
    setSearchTerm('');
    setPage(0);
  };

  const handleAddOrUpdatePurchase = (purchase) => {
    if (view === 'edit') {
      const updatedData = purchaseData.map(item =>
        item.id === selectedPurchase.id ? {
          ...item, // Include all original data
          id: item._id,
          projectName: item.project?.projectName || 'N/A',
          plotNumber: item.plotNo,
          khataNo: item.landDetails?.khataNumber || 'N/A',
          farmerName: item.sellers?.[0]?.farmerName || 'N/A',
          gataNo: item.landDetails?.gataOrKhasraNumber || 'N/A',
          totalArea: `${item.landDetails?.totalRakba || 0} ${item.landDetails?.rateUnit || 'Sq.Ft'}`,
          purchaseDate: item.purchaseAgreement?.registryDate ? new Date(item.purchaseAgreement.registryDate).toLocaleDateString() : 'N/A',
          mutationDate: item.purchaseAgreement?.mutationDate ? new Date(item.purchaseAgreement.mutationDate).toLocaleDateString() : 'N/A',
          purchaseAmount: `₹${item.purchaseAgreement?.totalDealAmount?.toLocaleString('en-IN') || '0'}`,
          paymentStatus: 'Completed',
          registrationStatus: 'Registered',
          kycDetails: {
            aadhar: item.sellers?.[0]?.aadhaar ? `XXXX-XXXX-${item.sellers[0].aadhaar.slice(-4)}` : 'N/A',
            pan: item.sellers?.[0]?.panNumber || 'N/A',
            phone: item.sellers?.[0]?.mobile || 'N/A',
            email: item.sellers?.[0]?.email || 'N/A',
            address: `${item.sellers?.[0]?.village || ''}, ${item.sellers?.[0]?.district || ''}, ${item.sellers?.[0]?.state || ''}`
          }
        } : item
      );
      setPurchaseData(updatedData);
    } else {
      fetchPurchaseData();
    }
    handleBackToList();
  };

  const buildExportRows = () =>
    filteredData.map((r, index) => ({
      'S.No': index + 1,
      'Project': r.projectName,
      'Plot': Array.isArray(r.plotNumber) ? r.plotNumber.join(', ') : (r.plotNumber || ''),
      'Farmer': r.farmerName,
      'Phone': r.kycDetails?.phone || 'N/A',
      'Khata No': r.khataNo,
      'Gata No': r.gataNo,
      'Total Area': r.totalArea,
      'Purchase Amount': r.purchaseAmount,
      'Purchase Date': r.purchaseDate,
      'Mutation Date': r.mutationDate,
    }));

  const handleExportPDF = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportPDFUtil({
      rows,
      title: 'Purchase Records',
      fileNameBase: 'purchase_records',
      orientation: 'l',
    });
  };

  const handleExportCSV = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportCSVUtil({ rows, fileNameBase: 'purchase_records' });
  };

  const handleExportExcel = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsExcel({ rows, sheetName: 'Purchase Records', fileNameBase: 'purchase_records' });
  };


  const handleDeletePurchase = (itemId) => {
    showConfirm(
      'Are you sure you want to delete this purchase record? This action cannot be undone.',
      async () => {
        try {
          setLoading(true);
          const response = await deleteLandOwnership(itemId);

          if (response.success) {
            setPurchaseData(prevData => prevData.filter(item => item.id !== itemId));
            showAlert('Purchase record deleted successfully', 'Success');
          } else {
            setError(response.message || 'Failed to delete purchase record');
            showAlert(response.message || 'Failed to delete purchase record', 'Error');
          }
        } catch (err) {
          setError(err.message || 'Failed to delete purchase record');
          showAlert(err.message || 'Failed to delete purchase record', 'Error');
        } finally {
          setLoading(false);
          setModalState(prev => ({ ...prev, confirm: { ...prev.confirm, open: false } }));
        }
      },
      'Delete Purchase',
      'error'
    );
  };

  const handleViewPurchase = async (itemId) => {
    try {
      setViewLoading(true);
      setViewModalOpen(true);
      setSelectedPurchase(null);

      // Fetch the complete record details from the API
      const response = await getLandOwnershipRecordById(itemId);

      if (response.success && response.data) {
        setSelectedPurchase({
          ...response.data,
          id: response.data._id,
          fullDetails: response.data
        });
      } else {
        setError(response.message || 'Purchase record not found');
        setViewModalOpen(false); // Close modal if failed
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch purchase details');
      setViewModalOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const openImagePreview = (url, title) => {
    if (!url) return;
    setImageDialogUrl(url);
    setImageDialogTitle(title || 'Image Preview');
    setImageDialogOpen(true);
  };

  const openImageInNewTab = (url) => {
    if (!url) return;
    try { window.open(url, '_blank', 'noopener,noreferrer'); } catch (e) { /* ignore */ }
  };

  const cleanImageUrl = (url) => {
    if (!url) return '';
    return typeof url === 'string' ? url.trim() : '';
  };

  const getLandDocuments = (record = {}) => (
    record?.purchase?.landDocuments ||
    record?.landDocuments ||
    record?.purchase?.documents ||
    record?.documents ||
    {}
  );

  const fetchPurchaseData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getAllLandOwnershipRecords({
        page: page + 1,
        limit: rowsPerPage
      });

      if (response.success) {
        const transformedData = response.data.map(item => ({
          ...item, // Include all original data
          id: item._id,
          projectName: item.project?.projectName || 'N/A',
          plotNumber: item?.plotNo,
          khataNo: item.landDetails?.khataNumber || 'N/A',
          farmerName: item.sellers?.[0]?.farmerName || 'N/A',
          gataNo: item.landDetails?.gataOrKhasraNumber || 'N/A',
          totalArea: `${item.landDetails?.totalRakba || 0} ${item.landDetails?.rateUnit || 'Sq.Ft'}`,
          purchaseDate: item.purchaseAgreement?.registryDate ? new Date(item.purchaseAgreement.registryDate).toLocaleDateString() : 'N/A',
          mutationDate: item.purchaseAgreement?.mutationDate ? new Date(item.purchaseAgreement.mutationDate).toLocaleDateString() : 'N/A',
          purchaseAmount: `₹${item.purchaseAgreement?.totalDealAmount?.toLocaleString('en-IN') || '0'}`,
          paymentStatus: 'Completed',
          registrationStatus: 'Registered',
          kycDetails: {
            aadhar: item.sellers?.[0]?.aadhaar ? `XXXX-XXXX-${item.sellers[0].aadhaar.slice(-4)}` : 'N/A',
            pan: item.sellers?.[0]?.panNumber || 'N/A',
            phone: item.sellers?.[0]?.mobile || 'N/A',
            email: item.sellers?.[0]?.email || 'N/A',
            address: `${item.sellers?.[0]?.village || ''}, ${item.sellers?.[0]?.district || ''}, ${item.sellers?.[0]?.state || ''}`
          }
        }));
        setPurchaseData(transformedData);
      } else {
        setError(response.message || 'Failed to fetch purchase data');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch purchase data');
    } finally {
      setLoading(false);
    }
  };



  const filteredData = purchaseData.filter(item =>
    (item.farmerName && item.farmerName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.projectName && item.projectName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  React.useEffect(() => {
    fetchPurchaseData();
  }, [page, rowsPerPage]);

  if (!canReadPurchase) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <Avatar sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Purchase Management. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  if (view === 'add' || view === 'edit') {
    return (
      <AddPurchasePage
        editMode={view === 'edit'}
        editData={selectedPurchase}
        onBack={handleBackToList}
        onAddPurchase={handleAddOrUpdatePurchase}
      />
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Typography variant="h4" fontWeight="bold">
          Purchase Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          {canWritePurchase && (
            <ExportButton
              onExportPdf={handleExportPDF}
              onExportCsv={handleExportCSV}
              onExportExcel={handleExportExcel}
              variant="outlined"
              sx={{
                px: 2.5,
                py: 1.2,
                fontSize: '15px'
              }}
            />
          )}
          {canWritePurchase && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenAdd}
              sx={{ px: 3, borderRadius: 2, width: { xs: '100%', sm: 'auto' }, textTransform: 'none', fontWeight: 600, height: { xs: 'auto', sm: 40 } }}
            >
              Add New Purchase
            </Button>
          )}
          <RefreshButton
            onClick={() => fetchPurchaseData()}
            loading={loading}
            sx={{
              fontWeight: 600,
              px: 2,
              fontSize: '15px',
              borderRadius: 2,
              height: { xs: 'auto', sm: 40 }
            }}
          />
        </Box>
      </Box>

      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                placeholder="Search by Farmer Name, Project, or Plot..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleReset}
                sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, height: '40px' }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <Typography>Loading purchase data...</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: '#f8f9fa', fontWeight: 'bold' } }}>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>S.No</TableCell>
                  <TableCell>Project & Plot</TableCell>
                  <TableCell>Farmer Details</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Land Info</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell align="center">{canWritePurchase ? 'Actions' : 'Status'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography>No purchase records found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item, index) => (
                      <TableRow key={item.id} hover>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{item.projectName}</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 0.5 }}>
                            {item.plotNumber?.length > 0 &&
                              item.plotNumber.map((plot, idx) => (
                                <Chip
                                  key={idx}
                                  label={plot}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.65rem', mr: 0.5, mb: 0.5 }}
                                />
                              ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: { xs: 24, sm: 30 }, height: { xs: 24, sm: 30 }, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>{item.farmerName?.[0] || 'U'}</Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{item.farmerName || 'N/A'}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>{item.kycDetails?.phone || 'N/A'}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          <Typography variant="caption" display="block">Khata: {item.khataNo}</Typography>
                          <Typography variant="caption" display="block">Gata: {item.gataNo}</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            {Object.entries(getLandDocuments(item)).map(([key, rawUrl]) => {
                              if (!rawUrl) return null;
                              const url = cleanImageUrl(rawUrl);
                              const isPdf = url.toLowerCase().endsWith('.pdf');
                              return (
                                <Tooltip key={key} title={key.replace(/([A-Z])/g, ' $1').trim()}>
                                  <Avatar
                                    variant="rounded"
                                    sx={{ width: 20, height: 20, cursor: 'pointer', border: '1px solid rgba(0,0,0,0.1)', bgcolor: isPdf ? '#ffebee' : 'transparent' }}
                                    onClick={() => openImagePreview(url, key.replace(/([A-Z])/g, ' $1').trim())}
                                  >
                                    {isPdf ? <PictureAsPdf sx={{ fontSize: 12, color: '#f44336' }} /> : <img src={url} alt={key} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                                  </Avatar>
                                </Tooltip>
                              );
                            })}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }}>{item.purchaseAmount || 'N/A'}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{item.totalArea || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewPurchase(item.id)}
                                color="primary"
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {/* <Tooltip title="Direct View Document">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => {
                                  const docs = item.purchase?.documents || item.documents || {};
                                  const rawUrl = docs.kahtuniCertificate || docs.purchaseAgreement || Object.values(docs)[0];
                                  if (rawUrl) window.open(cleanImageUrl(rawUrl), '_blank');
                                }}
                                disabled={!(item.purchase?.documents || item.documents)}
                              >
                                <ImageIcon fontSize="small" />
                              </IconButton>
                            </Tooltip> */}
                            {canWritePurchase && (
                              <>
                                <Tooltip title="Edit Page">
                                  <IconButton size="small" onClick={() => handleOpenEdit(item)} color="secondary">
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeletePurchase(item.id)}
                                    disabled={loading}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}

                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </Paper>
      )}

      <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} maxWidth="lg" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1b3bbb 0%, #2563eb 100%)', 
          color: 'white', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ReceiptLong sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">Purchase Details</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>ID: {selectedPurchase?.id?.slice(-8).toUpperCase() || 'N/A'}</Typography>
            </Box>
          </Box>
          <IconButton aria-label="close" onClick={() => setViewModalOpen(false)} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f8fafc', p: 0 }}>
          {viewLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 10, gap: 2 }}>
              <CircularProgress size={40} thickness={5} sx={{ color: '#2563eb' }} />
              <Typography fontWeight="medium" color="text.secondary">Fetching complete record details...</Typography>
            </Box>
          ) : (
            selectedPurchase && (
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Grid container spacing={3}>
                  {/* Top Summary Cards */}
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      {[
                        { label: 'Total Deal Amount', value: `₹${(selectedPurchase?.fullDetails?.summary?.totalDealAmount || selectedPurchase?.fullDetails?.purchaseAgreement?.totalDealAmount || 0).toLocaleString('en-IN')}`, icon: <AccountBalanceWallet sx={{ color: '#059669' }} />, bgcolor: '#ecfdf5' },
                        { label: 'Total Paid', value: `₹${(selectedPurchase?.fullDetails?.purchase?.totalPayment || selectedPurchase?.fullDetails?.summary?.totalPaid || 0).toLocaleString('en-IN')}`, icon: <CreditCardIcon sx={{ color: '#2563eb' }} />, bgcolor: '#eff6ff' },
                        { label: 'Outstanding', value: `₹${(selectedPurchase?.fullDetails?.purchase?.outstandingAmount || selectedPurchase?.fullDetails?.summary?.outstandingAmount || 0).toLocaleString('en-IN')}`, icon: <AccountBalance sx={{ color: '#dc2626' }} />, bgcolor: '#fef2f2' },
                        { label: 'Total Area', value: `${selectedPurchase?.fullDetails?.landDetails?.totalRakba || selectedPurchase?.fullDetails?.purchase?.landDetails?.totalRakba || 0} ${selectedPurchase?.fullDetails?.landDetails?.rateUnit || selectedPurchase?.fullDetails?.purchase?.landDetails?.rateUnit || 'Sq.Ft'}`, icon: <Straighten sx={{ color: '#7c3aed' }} />, bgcolor: '#f5f3ff' },
                      ].map((item, idx) => (
                        <Grid item xs={12} sm={6} md={3} key={idx}>
                          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: item.bgcolor, border: '1px solid rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 40, height: 40, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                              {item.icon}
                            </Avatar>
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block">{item.label}</Typography>
                              <Typography variant="subtitle1" fontWeight="bold">{item.value}</Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>

                  {/* Main Details Sections */}
                  {(() => {
                    const fd = selectedPurchase?.fullDetails;
                    const purchase = fd?.purchase || {};
                    const land = fd?.landDetails || purchase?.landDetails;
                    const docs = fd?.landDocuments || purchase?.landDocuments || fd?.documents || purchase?.documents;
                    const b = fd?.boundaries || purchase?.boundaries;
                    const l = fd?.legalStatus || purchase?.legalStatus;
                    const a = fd?.purchaseAgreement || purchase?.purchaseAgreement;
                    const s = fd?.sellers || fd?.owners || purchase?.sellers || purchase?.owners;
                    const sum = fd?.summary || purchase?.summary;

                    const DetailItem = ({ label, value, icon }) => (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {icon} {label}
                        </Typography>
                        <Typography variant="body2" fontWeight="600" color="#1e293b">{value || 'N/A'}</Typography>
                      </Box>
                    );

                    const SectionTitle = ({ title, icon }) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 1 }}>
                        <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
                        <Typography variant="button" fontWeight="800" color="primary.main" letterSpacing="1px">{title}</Typography>
                        <Divider sx={{ flexGrow: 1, ml: 1, opacity: 0.5 }} />
                      </Box>
                    );

                    return (
                      <>
                        {/* Land & Location Section */}
                        <Grid item xs={12} md={7}>
                          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', height: '100%' }}>
                            <CardContent sx={{ p: 3 }}>
                              <SectionTitle title="Property Details" icon={<MapIcon fontSize="small" />} />
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                  <DetailItem label="Project Name" value={purchase?.projectId?.projectName || 'N/A'} icon={<Business fontSize="inherit" />} />
                                  <DetailItem label="Plots" value={purchase?.plotNo?.join(', ')} icon={<LocationOn fontSize="inherit" />} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <DetailItem label="Location" value={`${land?.village || 'N/A'}, ${land?.tehsil || 'N/A'}`} icon={<LocationCity fontSize="inherit" />} />
                                  <DetailItem label="State/District" value={`${land?.state || 'N/A'} / ${land?.district || 'N/A'}`} icon={<MapIcon fontSize="inherit" />} />
                                </Grid>
                                <Grid item xs={12}><Divider sx={{ my: 1, borderStyle: 'dashed' }} /></Grid>
                                <Grid item xs={6} sm={4}><DetailItem label="Khata Number" value={land?.khataNumber} /></Grid>
                                <Grid item xs={6} sm={4}><DetailItem label="Gata Number" value={land?.gataOrKhasraNumber} /></Grid>
                                <Grid item xs={6} sm={4}><DetailItem label="Land Type" value={land?.landType} /></Grid>
                                <Grid item xs={6} sm={4}><DetailItem label="Usage" value={land?.landUsage} /></Grid>
                                <Grid item xs={6} sm={4}><DetailItem label="Total Area" value={`${land?.totalRakba || 0} ${land?.rateUnit || ''}`} /></Grid>
                                <Grid item xs={6} sm={4}><DetailItem label="Total Amount" value={`₹${land?.totalAmount?.toLocaleString('en-IN') || '0'}`} /></Grid>
                              </Grid>

                              <SectionTitle title="Land Documents" icon={<AttachFile fontSize="small" />} />
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {docs && Object.entries(docs).map(([key, rawUrl]) => {
                                  if (!rawUrl) return null;
                                  const url = cleanImageUrl(rawUrl);
                                  const isPdf = url.toLowerCase().endsWith('.pdf');
                                  const label = key.replace(/([A-Z])/g, ' $1').trim().replace(/^\w/, (c) => c.toUpperCase());
                                  
                                  return (
                                    <Tooltip key={key} title={`View ${label}`}>
                                      <Paper
                                        onClick={() => openImagePreview(url, label)}
                                        elevation={0}
                                        sx={{ 
                                          p: 1, borderRadius: 2, cursor: 'pointer', border: '1px solid #e2e8f0', transition: 'all 0.2s',
                                          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderColor: 'primary.light' }
                                        }}
                                      >
                                        <Box sx={{ width: 80, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: isPdf ? '#fef2f2' : '#f8fafc', borderRadius: 1.5, overflow: 'hidden' }}>
                                          {isPdf ? <PictureAsPdf sx={{ fontSize: 32, color: '#ef4444' }} /> : <img src={url} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                                        </Box>
                                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.65rem', fontWeight: 600, display: 'block', textAlign: 'center', width: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Typography>
                                      </Paper>
                                    </Tooltip>
                                  );
                                })}
                                {(!docs || Object.keys(docs).length === 0) && (
                                  <Typography variant="body2" color="text.secondary">No documents uploaded.</Typography>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* Agreement & Legal Section */}
                        <Grid item xs={12} md={5}>
                          <Grid container spacing={3}>
                            <Grid item xs={12}>
                              <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
                                <CardContent sx={{ p: 3 }}>
                                  <SectionTitle title="Agreement Details" icon={<Description fontSize="small" />} />
                                  <Grid container spacing={2}>
                                    <Grid item xs={6}><DetailItem label="Agreement Type" value={a?.agreementType} icon={<HistoryIcon fontSize="inherit" />} /></Grid>
                                    <Grid item xs={6}><DetailItem label="Registry Date" value={a?.registryDate ? new Date(a?.registryDate).toLocaleDateString() : 'N/A'} icon={<Visibility fontSize="inherit" />} /></Grid>
                                    <Grid item xs={6}><DetailItem label="Mutation Date" value={a?.mutationDate ? new Date(a?.mutationDate).toLocaleDateString() : 'N/A'} /></Grid>
                                    <Grid item xs={6}><DetailItem label="Stamp Value" value={`₹${a?.stampDutyValue?.toLocaleString('en-IN') || '0'}`} /></Grid>
                                  </Grid>

                                  <SectionTitle title="Legal Status" icon={<Policy fontSize="small" />} />
                                  <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                      <Chip 
                                        label={l?.legalStatus || 'Standard'} 
                                        size="small" 
                                        color={l?.legalStatus === 'Verified' ? 'success' : 'warning'} 
                                        sx={{ borderRadius: 1, fontWeight: 'bold', fontSize: '10px' }} 
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Box component="span" sx={{ display: 'flex', gap: 1 }}>
                                        {l?.encumbrance && <Chip label="Encumbered" size="small" variant="outlined" color="error" sx={{ height: 20, fontSize: '9px' }} />}
                                        {l?.courtCase && <Chip label="Court Case" size="small" variant="outlined" color="error" sx={{ height: 20, fontSize: '9px' }} />}
                                      </Box>
                                    </Grid>
                                  </Grid>

                                  <SectionTitle title="Boundaries" icon={<MapIcon fontSize="small" />} />
                                  <Box sx={{ p: 1.5, bgcolor: '#f1f5f9', borderRadius: 2 }}>
                                    <Grid container spacing={1}>
                                      <Grid item xs={6}><Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>EAST</Typography><Typography variant="body2" fontWeight="600">{b?.east || 'N/A'}</Typography></Grid>
                                      <Grid item xs={6}><Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>WEST</Typography><Typography variant="body2" fontWeight="600">{b?.west || 'N/A'}</Typography></Grid>
                                      <Grid item xs={6}><Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>NORTH</Typography><Typography variant="body2" fontWeight="600">{b?.north || 'N/A'}</Typography></Grid>
                                      <Grid item xs={6}><Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>SOUTH</Typography><Typography variant="body2" fontWeight="600">{b?.south || 'N/A'}</Typography></Grid>
                                    </Grid>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          </Grid>
                        </Grid>

                        {/* Sellers Section */}
                        <Grid item xs={12}>
                          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
                            <CardContent sx={{ p: 3 }}>
                              <SectionTitle title="Seller / Owner Information" icon={<Person fontSize="small" />} />
                              <Grid container spacing={3}>
                                {s && s.length > 0 ? s.map((seller, index) => (
                                  <Grid item xs={12} md={s.length > 1 ? 6 : 12} key={index}>
                                    <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #f1f5f9', bgcolor: '#fcfcfc' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Avatar sx={{ width: 50, height: 50, bgcolor: 'primary.main', fontWeight: 'bold', fontSize: 20 }}>
                                          {cleanImageUrl(seller.sellerPhoto) ? <img src={cleanImageUrl(seller.sellerPhoto)} alt="S" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (seller.farmerName?.[0] || 'S')}
                                        </Avatar>
                                        <Box>
                                          <Typography variant="subtitle1" fontWeight="bold">{seller.farmerName || seller.name || 'Seller Name'}</Typography>
                                          <Typography variant="caption" color="text.secondary">Father/Husband: {seller.fatherName || seller.fatherHusbandName || 'N/A'}</Typography>
                                        </Box>
                                        <Chip label={`Seller ${index + 1}`} size="small" variant="outlined" sx={{ ml: 'auto', fontWeight: 'bold' }} />
                                      </Box>
                                      <Grid container spacing={2}>
                                        <Grid item xs={6}><DetailItem label="Mobile" value={seller.mobile} icon={<Smartphone fontSize="inherit" />} /></Grid>
                                        <Grid item xs={6}><DetailItem label="Email" value={seller.email} icon={<EmailIcon fontSize="inherit" />} /></Grid>
                                        <Grid item xs={6}><DetailItem label="PAN" value={seller.panNumber} icon={<CreditCardIcon fontSize="inherit" />} /></Grid>
                                        <Grid item xs={6}><DetailItem label="Aadhaar" value={seller.aadhaar} icon={<Policy fontSize="inherit" />} /></Grid>
                                      </Grid>
                                      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {[
                                          { key: 'aadharFront', label: 'Aadhaar F' }, { key: 'aadharBack', label: 'Aadhaar B' },
                                          { key: 'pan', label: 'PAN' }, { key: 'signature', label: 'Signature' },
                                          { key: 'cancelledChequeUrl', label: 'Cheque' }
                                        ].map(doc => {
                                          const u = cleanImageUrl(seller[doc.key]);
                                          if (!u) return null;
                                          return (
                                            <Chip
                                              key={doc.key}
                                              icon={u.toLowerCase().endsWith('.pdf') ? <PictureAsPdf /> : <Visibility />}
                                              label={doc.label}
                                              onClick={() => openImagePreview(u, `${seller.farmerName || 'Seller'} ${doc.label}`)}
                                              size="small"
                                              variant="outlined"
                                              sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f1f5f9' } }}
                                            />
                                          );
                                        })}
                                      </Box>
                                    </Paper>
                                  </Grid>
                                )) : (
                                  <Grid item xs={12}><Typography variant="body2" color="text.secondary">No seller information available.</Typography></Grid>
                                )}
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>

                        {/* Payment Summary Section */}
                        <Grid item xs={12}>
                          <Paper 
                            elevation={0} 
                            sx={{ 
                              p: 3, 
                              borderRadius: 4, 
                              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', 
                              border: '1px solid #e2e8f0',
                              borderLeft: '5px solid #2563eb'
                            }}
                          >
                            <SectionTitle title="Payment Intelligence Summary" icon={<AccountBalanceWallet fontSize="small" />} />
                            <Grid container spacing={4} sx={{ mt: 1 }}>
                              <Grid item xs={12} md={4}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" fontWeight="700" letterSpacing="0.5px">TOTAL VALUATION</Typography>
                                  <Typography variant="h4" fontWeight="800" color="#1e293b">₹{(sum?.totalDealAmount || a?.totalDealAmount || 0).toLocaleString('en-IN')}</Typography>
                                  <Typography variant="caption" color="success.main" fontWeight="bold">Agreed Deal Value</Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" fontWeight="700" letterSpacing="0.5px">DISBURSED AMOUNT</Typography>
                                  <Typography variant="h4" fontWeight="800" color="#2563eb">₹{(purchase?.totalPayment || sum?.totalPaid || 0).toLocaleString('en-IN')}</Typography>
                                  <Typography variant="caption" color="primary.main" fontWeight="bold">Total Payments Processed</Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" fontWeight="700" letterSpacing="0.5px">LIABILITY / OUTSTANDING</Typography>
                                  <Typography variant="h4" fontWeight="800" color={ (purchase?.outstandingAmount || sum?.outstandingAmount || 0) > 0 ? "#dc2626" : "#059669"}>
                                    ₹{(purchase?.outstandingAmount || sum?.outstandingAmount || 0).toLocaleString('en-IN')}
                                  </Typography>
                                  <Typography variant="caption" color={(purchase?.outstandingAmount || sum?.outstandingAmount || 0) > 0 ? "error.main" : "success.main"} fontWeight="bold">
                                    {(purchase?.outstandingAmount || sum?.outstandingAmount || 0) > 0 ? "Pending Balance" : "Fully Settled"}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>
                      </>
                    );
                  })()}
                </Grid>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
          <Button onClick={() => setViewModalOpen(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}>
            Close
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Edit />} 
            onClick={() => { setViewModalOpen(false); handleOpenEdit(selectedPurchase); }}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            Edit Record
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {imageDialogTitle}
          <IconButton aria-label="close" onClick={() => setImageDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', p: 0, minHeight: '400px' }}>
          {imageDialogUrl ? (
            imageDialogUrl.toLowerCase().endsWith('.pdf') ? (
              <iframe src={imageDialogUrl} title="PDF Preview" width="100%" height="600px" style={{ border: 'none' }} />
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Box component="img" src={imageDialogUrl} alt={imageDialogTitle} sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
              </Box>
            )
          ) : (
            <Typography sx={{ p: 2 }}>No document available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => window.open(imageDialogUrl, '_blank')} color="primary">Open in New Tab</Button>
          <Button onClick={() => setImageDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <AlertModal
        open={modalState.alert.open}
        title={modalState.alert.title}
        message={modalState.alert.message}
        onClose={() => setModalState(prev => ({ ...prev, alert: { ...prev.alert, open: false } }))}
      />

      <ConfirmModal
        open={modalState.confirm.open}
        title={modalState.confirm.title}
        type={modalState.confirm.type}
        onClose={() => setModalState(prev => ({ ...prev, confirm: { ...prev.confirm, open: false } }))}
        onConfirm={modalState.confirm.onConfirm}
      >
        {modalState.confirm.message}
      </ConfirmModal>
    </Box>
  );
};

export default Purchase;
